
import React, { lazy, Suspense, useRef, useState } from "react";
import styles from "./CompanyOtpVerify.module.css";

const Button = lazy(() => import("../Dealers/Button"));

const OTP_LENGTH = 6;

const AccountsOtpVerify = ({
  mode,
  createAccountsData,
  onClose,
  adminOtp,
  setAdminOtp,
  accountsOtp,
  setAccountsOtp,
  loading,
}) => {
  const [error, setError] = useState("");

  const adminRefs = useRef([]);
  const settingsRefs = useRef([]);

  // ─── OTP digit change ──────────────────────────────────────────────────────
  const handleChange = (e, index, type) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) return;

    const otp = type === "admin" ? [...adminOtp] : [...accountsOtp];
    otp[index] = value;

    if (type === "admin") {
      setAdminOtp(otp);
    } else {
      setAccountsOtp(otp);
    }

    // Auto-advance focus to next input
    if (value && index < OTP_LENGTH - 1) {
      const refs = type === "admin" ? adminRefs.current : settingsRefs.current;
      refs[index + 1]?.focus();
    }
  };

  // ─── Backspace — move focus back ──────────────────────────────────────────
  const handleKeyDown = (e, index, type) => {
    const refs = type === "admin" ? adminRefs.current : settingsRefs.current;
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      refs[index - 1]?.focus();
    }
  };

  // ─── Paste — fill all boxes at once ───────────────────────────────────────
  const handlePaste = (e, type) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pastedData) return;

    const otpArray = pastedData.split("");
    while (otpArray.length < OTP_LENGTH) otpArray.push("");

    if (type === "admin") {
      setAdminOtp(otpArray);
    } else {
      setAccountsOtp(otpArray);
    }
  };

  // ─── Local validation before calling parent's createSettingsData ──────────
  const handleVerifyClick = () => {
    setError("");

    if (adminOtp.some((d) => d === "")) {
      setError("Please enter the complete Admin OTP.");
      return;
    }
    if (accountsOtp.some((d) => d === "")) {
      setError("Please enter the complete Profile OTP.");
      return;
    }

    createAccountsData();
  };

  // ─── Render a row of OTP input boxes ──────────────────────────────────────
  const renderOtpInputs = (otp, type, refs) => (
    <div className={styles.otpContainer}>
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (refs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          className={styles.otpInput}
          onChange={(e) => handleChange(e, index, type)}
          onKeyDown={(e) => handleKeyDown(e, index, type)}
          onPaste={(e) => handlePaste(e, type)}
        />
      ))}
    </div>
  );

  return (
    <div className={`${styles.modalCard} ${mode ? styles.dark : styles.light}`}>
      {/* Close button */}
      <button className={styles.closeBtn} onClick={onClose} type="button">
        ×
      </button>

      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>OTP Verification</h2>
        <p className={styles.subTitle}>
          OTPs have been sent to both Admin and Accounts email addresses.
        </p>
      </div>

      {/* Admin OTP */}
      <div className={styles.section}>
        <label className={styles.label}>Admin Email OTP</label>
        {renderOtpInputs(adminOtp, "admin", adminRefs)}
      </div>

      {/* Accounts / Profile OTP */}
      <div className={styles.section}>
        <label className={styles.label}>Profile Email OTP</label>
        {renderOtpInputs(accountsOtp, "settings", settingsRefs)}
      </div>

      {/* Inline error */}
      {error && <div className={styles.errorBox}>{error}</div>}

      {/* Verify & Save */}
      <div className={styles.footer}>
        <Suspense fallback={<button>Loading...</button>}>
          <Button
            type="button"
            buttonClick={handleVerifyClick}
            disabled={loading}
            buttonText={loading ? "Verifying..." : "Verify & Save"}
            buttonBgColor="#4164E3"
            buttonTextColor="#ffffff"
            buttonBorder="none"
            loading={loading}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default AccountsOtpVerify;
